import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

const ConfirmPracticeDetailsStep = ({
  practiceNpiData,
  details,
  errors,
  onDetailChange,
  onCopyBilling,
  onCopyCorporateToBilling,
  onCopyCorporateToShipping,
  onBack,
  onContinue,
  continueLabel = 'Continue'
}) => (
  <Box sx={{ maxWidth: 400 }}>
    <Typography variant="h6" gutterBottom>
      Confirm practice details
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Please review and update your practice information below.
    </Typography>
    {practiceNpiData && (
      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Practice NPI Number"
          value={practiceNpiData.number}
          disabled
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Practice Name (Legal Name)"
          value={details.practice_name}
          onChange={onDetailChange('practice_name')}
          error={!!errors.practice_name}
          helperText={errors.practice_name}
          required
          data-error={!!errors.practice_name}
          sx={{ mb: 2 }}
        />
        {/* Corporate Address Section */}
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
          Corporate address
        </Typography>
        <TextField
          fullWidth
          label="Street Address"
          value={details.corporate_street_1}
          onChange={onDetailChange('corporate_street_1')}
          error={!!errors.corporate_street_1}
          helperText={errors.corporate_street_1}
          required
          data-error={!!errors.corporate_street_1}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Address Line 2"
          value={details.corporate_street_2}
          onChange={onDetailChange('corporate_street_2')}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="City"
            value={details.corporate_city}
            onChange={onDetailChange('corporate_city')}
            error={!!errors.corporate_city}
            helperText={errors.corporate_city}
            required
            data-error={!!errors.corporate_city}
          />
          <TextField
            fullWidth
            label="State"
            value={details.corporate_state}
            onChange={onDetailChange('corporate_state')}
            error={!!errors.corporate_state}
            helperText={errors.corporate_state}
            required
            data-error={!!errors.corporate_state}
          />
          <TextField
            fullWidth
            label="ZIP Code"
            value={details.corporate_zip_code}
            onChange={onDetailChange('corporate_zip_code')}
            error={!!errors.corporate_zip_code}
            helperText={errors.corporate_zip_code}
            required
            data-error={!!errors.corporate_zip_code}
          />
        </Box>
        <Box sx={{ textAlign: 'right', mb: 2 }}>
          <Button variant="outlined" size="small" onClick={onCopyCorporateToBilling}>
            Copy Corporate Address to Billing Address
          </Button>
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
          Billing address
        </Typography>
        <TextField
          fullWidth
          label="Street Address"
          value={details.billing_street_1}
          onChange={onDetailChange('billing_street_1')}
          error={!!errors.billing_street_1}
          helperText={errors.billing_street_1}
          required
          data-error={!!errors.billing_street_1}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Address Line 2"
          value={details.billing_street_2}
          onChange={onDetailChange('billing_street_2')}
          error={!!errors.billing_street_2}
          helperText={errors.billing_street_2}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="City"
            value={details.billing_city}
            onChange={onDetailChange('billing_city')}
            error={!!errors.billing_city}
            helperText={errors.billing_city}
            required
            data-error={!!errors.billing_city}
          />
          <TextField
            fullWidth
            label="State"
            value={details.billing_state}
            onChange={onDetailChange('billing_state')}
            error={!!errors.billing_state}
            helperText={errors.billing_state}
            required
            data-error={!!errors.billing_state}
          />
          <TextField
            fullWidth
            label="ZIP Code"
            value={details.billing_zip_code}
            onChange={onDetailChange('billing_zip_code')}
            error={!!errors.billing_zip_code}
            helperText={errors.billing_zip_code}
            required
            data-error={!!errors.billing_zip_code}
          />
        </Box>
        <Box sx={{ textAlign: 'right', mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={onCopyBilling}>
            Copy Billing Address to Shipping Address
          </Button>
          <Button variant="outlined" size="small" onClick={onCopyCorporateToShipping}>
            Copy Corporate Address to Shipping Address
          </Button>
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
          Shipping address (where we will send kits)
        </Typography>
        <TextField
          fullWidth
          label="Street Address"
          value={details.shipping_street_1}
          onChange={onDetailChange('shipping_street_1')}
          error={!!errors.shipping_street_1}
          helperText={errors.shipping_street_1}
          required
          data-error={!!errors.shipping_street_1}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Address Line 2"
          value={details.shipping_street_2}
          onChange={onDetailChange('shipping_street_2')}
          error={!!errors.shipping_street_2}
          helperText={errors.shipping_street_2}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="City"
            value={details.shipping_city}
            onChange={onDetailChange('shipping_city')}
            error={!!errors.shipping_city}
            helperText={errors.shipping_city}
            required
            data-error={!!errors.shipping_city}
          />
          <TextField
            fullWidth
            label="State"
            value={details.shipping_state}
            onChange={onDetailChange('shipping_state')}
            error={!!errors.shipping_state}
            helperText={errors.shipping_state}
            required
            data-error={!!errors.shipping_state}
          />
          <TextField
            fullWidth
            label="ZIP Code"
            value={details.shipping_zip_code}
            onChange={onDetailChange('shipping_zip_code')}
            error={!!errors.shipping_zip_code}
            helperText={errors.shipping_zip_code}
            required
            data-error={!!errors.shipping_zip_code}
          />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
          Practice information
        </Typography>
        <TextField
          fullWidth
          label="DBA"
          value={details.practice_dba}
          onChange={onDetailChange('practice_dba')}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="EIN"
          value={details.practice_ein}
          onChange={onDetailChange('practice_ein')}
          error={!!errors.practice_ein}
          helperText={errors.practice_ein}
          required
          data-error={!!errors.practice_ein}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={details.practice_request_tax_exemption}
              onChange={(e) => onDetailChange('practice_request_tax_exemption')(e)}
            />
          }
          label="Request tax exemption"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="State Tax ID"
          value={details.practice_state_tax_id}
          onChange={onDetailChange('practice_state_tax_id')}
          error={details.practice_request_tax_exemption && !!errors.practice_state_tax_id}
          helperText={details.practice_request_tax_exemption ? errors.practice_state_tax_id : ''}
          required={details.practice_request_tax_exemption}
          data-error={details.practice_request_tax_exemption && !!errors.practice_state_tax_id}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          freeSolo
          options={practiceNpiData.taxonomies?.map(t => t.desc) || []}
          value={details.practice_type}
          onChange={(e, newValue) => onDetailChange('practice_type')({ target: { value: newValue } })}
          renderInput={(params) => (
            <TextField {...params} label="Practice Type" fullWidth sx={{ mb: 2 }} />
          )}
        />
        <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'left' }}>
          Contact Information
        </Typography>
        <TextField
          fullWidth
          label="Phone Number"
          value={details.practice_phone}
          onChange={onDetailChange('practice_phone')}
          error={!!errors.practice_phone}
          helperText={errors.practice_phone}
          required
          data-error={!!errors.practice_phone}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Fax Number"
          value={details.practice_fax}
          onChange={onDetailChange('practice_fax')}
          error={!!errors.practice_fax}
          helperText={errors.practice_fax}
          required
          data-error={!!errors.practice_fax}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Email"
          value={details.practice_email}
          onChange={onDetailChange('practice_email')}
          error={!!errors.practice_email}
          helperText={errors.practice_email}
          required
          data-error={!!errors.practice_email}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Website"
          value={details.practice_website}
          onChange={onDetailChange('practice_website')}
          error={!!errors.practice_website}
          helperText={errors.practice_website}
          sx={{ mb: 2 }}
        />
      </Box>
    )}
    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
      <Button variant="outlined" onClick={onBack}>
        Back
      </Button>
      <Button variant="contained" onClick={onContinue}>
        {continueLabel}
      </Button>
    </Box>
  </Box>
);

export default ConfirmPracticeDetailsStep;
